import { useCallback, useEffect, useState } from "react";
import { Circles } from "react-loader-spinner";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import baseUrl from "../../../api/baseUrl";
import SmsPayButton from "../../../components/sms/SmsPayButton";
import SmsSettingsPanel from "./SmsSettingsPanel";
import {
  purchaseStatusBadgeClass,
  purchaseStatusLabel,
} from "../../../utils/sms/purchaseStatus";
import { formatSmsTypeLabel } from "./smsSettingsConfig";
import {
  DetailRow,
  DetailsBlock,
  MetricTile,
  PanelCard,
  SectionHeader,
} from "../components/accountUi";

export type SmsBalanceData = {
  configured?: boolean;
  accBalance?: number | null;
  tokenCredits?: number;
  totalAvailable?: number;
  balanceActive?: boolean | null;
  balanceCheckedAt?: string | null;
  balanceError?: string | null;
  lowBalance?: boolean;
  lowBalanceThreshold?: number;
};

type SmsPackageOption = {
  _id: string;
  name: string;
  tokens: number;
  price: number;
  description?: string;
};

type SmsPurchaseRow = {
  _id: string;
  orderId: string;
  packageName: string;
  tokens: number;
  amount: number;
  status: string;
  statusMessage?: string | null;
  paidAt?: string;
  createdAt: string;
};

export type SmsUsageStats = {
  last7Days?: number;
  last30Days?: number;
  thisMonth?: number;
  lastSentAt?: string | null;
  lastSentType?: string | null;
  byType?: Record<string, number>;
};

export type SmsUsageLogRow = {
  _id?: string;
  type?: string;
  recipient?: string;
  status?: string;
  providerStatus?: string;
  errorMessage?: string;
  sentAt?: string;
};

function formatSmsBalance(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatSmsDate(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function maskRecipient(value: string | null | undefined) {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 4) return value;
  return `${"*".repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}

function formatSmsType(value: string | null | undefined) {
  return formatSmsTypeLabel(value);
}

function formatSmsStatus(value: string | null | undefined) {
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

type SmsTabPanelProps = {
  balance: SmsBalanceData | null;
  usage: SmsUsageStats | null;
  smsLoading: boolean;
  smsError: string | null;
  onRefresh: (refreshBalance?: boolean) => void;
};

export default function SmsTabPanel({
  balance,
  usage,
  smsLoading,
  smsError,
  onRefresh,
}: SmsTabPanelProps) {
  const [logs, setLogs] = useState<SmsUsageLogRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [packages, setPackages] = useState<SmsPackageOption[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<any>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [purchases, setPurchases] = useState<SmsPurchaseRow[]>([]);
  const [grants, setGrants] = useState<
    { _id: string; tokens: number; note?: string; createdAt: string }[]
  >([]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const loadPackages = useCallback(() => {
    setPackagesLoading(true);
    baseUrl
      .get("/client/sms-packages")
      .then((res) => {
        if (res?.data?.success) {
          setPackages(res.data.data ?? []);
        }
      })
      .catch(() => setPackages([]))
      .finally(() => setPackagesLoading(false));
  }, []);

  const loadPurchases = useCallback(() => {
    baseUrl
      .get("/client/sms-packages/purchases")
      .then((res) => {
        if (res?.data?.success) {
          setPurchases(res.data.data ?? []);
        }
      })
      .catch(() => setPurchases([]));
    baseUrl
      .get("/client/sms-packages/grants")
      .then((res) => {
        if (res?.data?.success) {
          setGrants(res.data.data ?? []);
        }
      })
      .catch(() => setGrants([]));
  }, []);

  useEffect(() => {
    if (balance?.configured) {
      loadPackages();
      loadPurchases();
    }
  }, [balance?.configured, loadPackages, loadPurchases]);

  const startCheckout = async (packageId: string) => {
    setSelectedPackageId(packageId);
    setCheckout(null);
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const res = await baseUrl.post("/client/sms-packages/checkout", { packageId });
      if (res?.data?.success) {
        setCheckout(res.data.data);
      } else {
        setCheckoutError("Could not start checkout.");
      }
    } catch (err: any) {
      setCheckoutError(err?.response?.data?.message || "Could not start checkout.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePaymentEnded = () => {
    setCheckout(null);
    setSelectedPackageId(null);
    setCheckoutLoading(false);
    loadPurchases();
  };

  const fetchLogs = useCallback((nextPage: number) => {
    setLogsLoading(true);
    baseUrl
      .get("/client/account/sms-usage", { params: { page: nextPage } })
      .then((res) => {
        if (res?.data?.success) {
          setLogs(res.data.data?.logs ?? []);
          setPage(res.data.data?.page ?? nextPage);
          setTotalPages(res.data.data?.totalPages ?? 1);
        } else {
          setLogs([]);
        }
      })
      .catch(() => setLogs([]))
      .finally(() => setLogsLoading(false));
  }, []);

  useEffect(() => {
    if (!smsLoading) {
      fetchLogs(1);
    }
  }, [smsLoading, fetchLogs]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-white">
            SMS token usage
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Token balance and messages sent from your clinic
          </p>
        </div>
        <button
          type="button"
          onClick={() => onRefresh(true)}
          disabled={smsLoading}
          className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:self-auto"
        >
          <RefreshIcon
            sx={{ fontSize: 16 }}
            className={smsLoading ? "animate-spin text-primary" : ""}
          />
          Refresh balance
        </button>
      </div>

      {smsLoading && !balance ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Circles height="44" width="44" color="#174d7a" ariaLabel="loading sms" />
          <p className="text-sm text-slate-500">Loading SMS usage…</p>
        </div>
      ) : smsError ? (
        <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
          {smsError}
        </div>
      ) : balance == null ? (
        <PanelCard className="p-5">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            SMS usage data is not available right now. Try Refresh balance or reload the page.
          </p>
        </PanelCard>
      ) : (
        <>
          {!balance.configured ? (
            <PanelCard className="p-5">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                SMS sending is not fully set up yet. Configure your message templates below to
                start using SMS.
              </p>
            </PanelCard>
          ) : null}

          <SmsSettingsPanel onSaved={() => onRefresh(true)} />

          {balance.configured ? (
            <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <MetricTile
              label="Available SMS tokens"
              value={formatSmsBalance(balance.totalAvailable ?? balance.tokenCredits ?? 0)}
              accent
            />
            <MetricTile label="Sent (30 days)" value={usage?.last30Days ?? 0} />
            <MetricTile label="Sent (7 days)" value={usage?.last7Days ?? 0} />
          </div>

          <PanelCard className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
              <SectionHeader
                icon={ShoppingCartOutlinedIcon}
                title="Buy SMS tokens"
                subtitle="Pay securely with your card via PayHere"
              />
            </div>
            <div className="p-5 space-y-4">
              {packagesLoading ? (
                <p className="text-sm text-slate-500">Loading packages…</p>
              ) : packages.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No SMS packages available yet. Contact your platform admin.
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {packages.map((pkg) => (
                    <div
                      key={pkg._id}
                      className={`rounded-xl border p-4 transition ${
                        selectedPackageId === pkg._id
                          ? "border-primary bg-primary/5"
                          : "border-slate-200 dark:border-slate-600"
                      }`}
                    >
                      <p className="font-semibold text-slate-800 dark:text-white">{pkg.name}</p>
                      <p className="mt-1 text-2xl font-bold text-primary">
                        {pkg.tokens.toLocaleString()} <span className="text-sm font-medium">tokens</span>
                      </p>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                        Rs. {pkg.price.toLocaleString()}
                      </p>
                      {pkg.description ? (
                        <p className="mt-1 text-xs text-slate-500">{pkg.description}</p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => startCheckout(pkg._id)}
                        disabled={checkoutLoading && selectedPackageId === pkg._id}
                        className="mt-3 w-full rounded-lg border border-primary px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/5 disabled:opacity-50"
                      >
                        {checkoutLoading && selectedPackageId === pkg._id
                          ? "Preparing…"
                          : "Select package"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {checkoutError ? (
                <p className="text-sm text-red-600">{checkoutError}</p>
              ) : null}
              {checkout ? (
                <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800/50 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {checkout.packageName} — {checkout.tokens?.toLocaleString()} tokens
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      Total: Rs. {checkout.amount}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Tokens are added to your account after payment is confirmed.
                    </p>
                  </div>
                  <SmsPayButton
                    checkout={checkout}
                    disabled={checkoutLoading}
                    onPaying={() => setCheckoutLoading(true)}
                    onPaymentEnded={handlePaymentEnded}
                  />
                </div>
              ) : null}
            </div>
          </PanelCard>

          {purchases.length > 0 ? (
            <PanelCard className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
                <SectionHeader
                  icon={ShoppingCartOutlinedIcon}
                  title="Purchase history"
                  subtitle="Your SMS package orders"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/80 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Package</th>
                      <th className="px-4 py-3">Tokens</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((row) => (
                      <tr key={row._id} className="border-t border-slate-100 dark:border-slate-700/80">
                        <td className="px-4 py-3">
                          {formatSmsDate(row.paidAt || row.createdAt)}
                        </td>
                        <td className="px-4 py-3">{row.packageName}</td>
                        <td className="px-4 py-3">{row.tokens.toLocaleString()}</td>
                        <td className="px-4 py-3">Rs. {row.amount.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${purchaseStatusBadgeClass(row.status)}`}
                          >
                            {purchaseStatusLabel(row.status, row.statusMessage)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PanelCard>
          ) : null}

          {grants.length > 0 ? (
            <PanelCard className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
                <SectionHeader
                  icon={SmsOutlinedIcon}
                  title="Free token grants"
                  subtitle="Complimentary SMS tokens from your platform admin"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/80 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Tokens</th>
                      <th className="px-4 py-3">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((row) => (
                      <tr key={row._id} className="border-t border-slate-100 dark:border-slate-700/80">
                        <td className="px-4 py-3">{formatSmsDate(row.createdAt)}</td>
                        <td className="px-4 py-3 font-medium text-emerald-700">
                          +{row.tokens.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">{row.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PanelCard>
          ) : null}

          {(balance.totalAvailable ?? balance.tokenCredits ?? 0) <= 0 ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-red-200/80 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-200">
              <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />
              SMS tokens finished — buy a package below to continue sending messages
            </div>
          ) : balance.lowBalance ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
              <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />
              Low SMS balance
              {balance.lowBalanceThreshold != null
                ? ` (below ${formatSmsBalance(balance.lowBalanceThreshold)})`
                : ""}
            </div>
          ) : null}

          <PanelCard className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
              <SectionHeader
                icon={SmsOutlinedIcon}
                title="Usage summary"
                subtitle="Recent send activity for your clinic"
              />
            </div>
            <div className="px-2 py-2">
              <DetailsBlock>
                <DetailRow
                  label="Last SMS sent"
                  value={
                    usage?.lastSentAt
                      ? `${formatSmsDate(usage.lastSentAt)}${
                          usage.lastSentType ? ` (${formatSmsType(usage.lastSentType)})` : ""
                        }`
                      : "—"
                  }
                />
              </DetailsBlock>
            </div>
          </PanelCard>

          {usage?.byType && Object.keys(usage.byType).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {Object.entries(usage.byType).map(([type, count]) => (
                <span
                  key={type}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                >
                  {formatSmsType(type)}: {count}
                </span>
              ))}
            </div>
          ) : null}

          <PanelCard className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
              <SectionHeader
                icon={SmsOutlinedIcon}
                title="Recent SMS log"
                subtitle="Messages sent from your clinic account"
              />
            </div>
            <div className="overflow-x-auto">
              {logsLoading && logs.length === 0 ? (
                <p className="px-5 py-8 text-sm text-slate-500">Loading SMS log…</p>
              ) : logs.length === 0 ? (
                <p className="px-5 py-8 text-sm text-slate-500">
                  No SMS usage recorded yet.
                </p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/80 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Recipient</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log._id}
                        className="border-t border-slate-100 dark:border-slate-700/80"
                      >
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          {formatSmsDate(log.sentAt)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          {formatSmsType(log.type)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          {maskRecipient(log.recipient)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              log.status === "success"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                : log.status === "failed"
                                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                  : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                            }`}
                          >
                            {formatSmsStatus(log.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {totalPages > 1 ? (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 dark:border-slate-700/80">
                <button
                  type="button"
                  disabled={page <= 1 || logsLoading}
                  onClick={() => fetchLogs(page - 1)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                >
                  Previous
                </button>
                <span className="text-xs text-slate-500">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages || logsLoading}
                  onClick={() => fetchLogs(page + 1)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                >
                  Next
                </button>
              </div>
            ) : null}
          </PanelCard>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
